import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react'
import { useNavigate } from 'react-router-dom'
import { Area, AreaChart, ResponsiveContainer } from 'recharts'
import type { LucideIcon } from 'lucide-react'
import {
  AlertCircle,
  AlertTriangle,
  CalendarDays,
  ClipboardList,
  Clock,
  IndianRupee,
  MessageCircle,
  Plus,
  Scale,
  Users,
  Wallet,
} from 'lucide-react'
import { adminApi, unwrap as unwrapPayload } from '../services/api'
import { SERVER_ERROR_MESSAGE, SERVER_TIMEOUT_MESSAGE, withServerTimeout } from '../components/common/loadErrorUtils'
import ToastStack from '../components/common/ToastStack'
import { useSocket } from '../hooks/useSocket'
import { useColors } from '../hooks/useColors'

type ActiveFilter = 'all' | 'placed' | 'in_progress' | 'delivered'

/** One tile in the dashboard's stat row. `onClick` makes the tile a link. */
interface StatCard {
  // lucide's own icon type - the tiles pass strokeWidth as well as size/color.
  icon: LucideIcon
  value: string
  label: string
  trendText: string
  trendBg: string
  trendColor: string
  progressBg: string
  progressWidth: string
  valueColor: string
  onClick?: () => void
}

interface DashboardStats {
  orders_today: number
  revenue_today: number
  active_agents: number
  pending_pickups: number
  total_agents?: number
  /**
   * Value of the orders PLACED today, at their FINAL amount (manual adjustments
   * included) and excluding cancelled/failed/refunded orders. A different
   * question from `collected_today`, which is money actually received - never
   * substitute one for the other.
   */
  order_value_today?: number
  collected_today?: number
  /** COD orders still unpaid, all dates. Same definition as the Orders filter. */
  pending_cod_count?: number
  pending_cod_amount?: number
  /**
   * Bags already delivered whose COD was never marked collected. A SUBSET of
   * pending COD, not an addition to it - the actionable part ops must chase.
   */
  delivered_unmarked_cod_count?: number
  delivered_unmarked_cod_amount?: number
  /** Orders placed today excluding cancelled/failed/refunded. */
  billable_orders_today?: number
  // Laundry washed and delivered, in kg. `total` prefers the hub's recorded
  // weight per order and falls back to the booked weight; `actual` counts only
  // orders actually weighed at the hub.
  kg_washed_total?: number
  kg_orders_delivered?: number
  kg_orders_weighed?: number
}

interface DashboardAlert {
  type: string
  severity: 'high' | 'medium' | 'low'
  count?: number
  amount?: number
  message: string
  action?: string
  url?: string
}

interface AnalyticsData {
  labels: string[]
  revenue: number[]
  orders: number[]
}

interface DashboardOrder {
  id: string
  order_number: string
  customer_name?: string | null
  customer_phone?: string | null
  service_name?: string | null
  agent_name?: string | null
  status: string
  total: number
  items?: Array<{ service_name?: string; name?: string }>
}

interface OrdersPayload {
  orders?: DashboardOrder[]
}

interface ToastItem {
  id: string
  message: string
  onClick?: () => void
}

const CARD_SHADOW = '0px 2px 8px rgba(0,0,0,0.04), 0px 1px 4px rgba(0,0,0,0.04)'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
  }).format(Number(value || 0))

// Operational money figures are reconciled against cash, so they are shown to
// the paisa rather than rounded to whole rupees.
const formatExactCurrency = (value: number) => {
  const rounded = Math.round(Number(value || 0) * 100) / 100
  const hasPaise = Math.round(rounded * 100) % 100 !== 0
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(rounded)
}

const getStatusBadgeStyle = (status: string) => {
  const normalized = status.toLowerCase()

  if (normalized === 'placed') {
    return {
      background: 'rgba(214,185,123,0.12)',
      color: 'var(--accent)',
      border: '1px solid var(--accent)',
    }
  }

  if (['agent_assigned', 'picked_up', 'at_facility', 'in_wash', 'in_iron', 'quality_check', 'ready', 'out_for_delivery'].includes(normalized)) {
    return {
      background: 'var(--color-info-bg)',
      color: 'var(--color-info)',
      border: '1px solid var(--color-info-border)',
    }
  }

  if (normalized === 'delivered') {
    return {
      background: 'var(--color-success-bg)',
      color: 'var(--color-success)',
      border: '1px solid var(--color-success-border)',
    }
  }

  if (normalized === 'cancelled') {
    return {
      background: 'var(--color-error-bg)',
      color: 'var(--color-error)',
      border: '1px solid var(--color-error-border)',
    }
  }

  return {
    background: 'var(--surface)',
    color: 'var(--text-secondary)',
    border: '1px solid var(--border)',
  }
}

const getFirstService = (order: DashboardOrder) => {
  if (order.service_name) {
    return order.service_name
  }

  if (Array.isArray(order.items) && order.items.length > 0) {
    return order.items[0]?.service_name || order.items[0]?.name || '-'
  }

  return '-'
}

const toTitleCase = (value: string) =>
  value
    .replace(/_/g, ' ')
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')


function SkeletonCard() {
  const colors = useColors()

  return (
    <div
      className="rapidry-pulse"
      style={{
        flex: 1,
        minHeight: '168px',
        borderRadius: '16px',
        background: colors.surface,
      }}
    />
  )
}

function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  const colors = useColors()

  return (
    <div
      className="font-body"
      style={{
        background: colors.bgCard,
        border: '1px solid var(--color-error-border)',
        color: 'var(--color-error)',
        borderRadius: '12px',
        padding: '14px',
        fontSize: '13px',
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
      }}
    >
      <span>{message}</span>
      <button
        type="button"
        onClick={onRetry}
        style={{
          border: 'none',
          background: colors.accent,
          color: colors.bgSidebar,
          borderRadius: '999px',
          padding: '6px 12px',
          fontSize: '12px',
          fontWeight: 700,
          cursor: 'pointer',
          flexShrink: 0,
        }}
      >
        Retry
      </button>
    </div>
  )
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const colors = useColors()
  const [stats, setStats] = useState<DashboardStats>({
    orders_today: 0,
    revenue_today: 0,
    active_agents: 0,
    pending_pickups: 0,
  })
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    labels: [],
    revenue: [],
    orders: [],
  })
  const [alerts, setAlerts] = useState<DashboardAlert[]>([])
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number>(Date.now())
  const [timeNow, setTimeNow] = useState<number>(Date.now())
  const [recentOrders, setRecentOrders] = useState<DashboardOrder[]>([])
  const [statsLoading, setStatsLoading] = useState(true)
  const [statsError, setStatsError] = useState('')
  const [analyticsLoading, setAnalyticsLoading] = useState(true)
  const [analyticsError, setAnalyticsError] = useState('')
  const [ordersLoading, setOrdersLoading] = useState(true)
  const [ordersError, setOrdersError] = useState('')
  const [alertsLoading, setAlertsLoading] = useState(true)
  const [alertsError, setAlertsError] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [toasts, setToasts] = useState<ToastItem[]>([])
  const toastTimers = useRef<Record<string, number>>({})

  // Orders placed today that will never be fulfilled. Shown as context on the
  // Today's Orders tile so the excluded ones are visible rather than just missing.
  const cancelledToday = Math.max(
    0,
    Number(stats.orders_today || 0) - Number(stats.billable_orders_today ?? stats.orders_today ?? 0)
  )

  const getLoadMessage = (loadError: unknown) =>
    loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE

  const loadStats = useCallback(async () => {
    setStatsLoading(true)
    setStatsError('')

    try {
      const statsResponse = await withServerTimeout(adminApi.getDashboardStats())
      const statsPayload = unwrapPayload<DashboardStats>(statsResponse)
      setStats({
        orders_today: Number(statsPayload?.orders_today || 0),
        revenue_today: Number(statsPayload?.revenue_today || 0),
        active_agents: Number(statsPayload?.active_agents || 0),
        pending_pickups: Number(statsPayload?.pending_pickups || 0),
        order_value_today: Number(statsPayload?.order_value_today || 0),
        collected_today: Number(statsPayload?.collected_today || 0),
        pending_cod_count: Number(statsPayload?.pending_cod_count || 0),
        pending_cod_amount: Number(statsPayload?.pending_cod_amount || 0),
        // Left undefined when the API does not send them - an older backend
        // must fall back to the legacy field, not display a fabricated 0.
        delivered_unmarked_cod_count: statsPayload?.delivered_unmarked_cod_count,
        delivered_unmarked_cod_amount: statsPayload?.delivered_unmarked_cod_amount,
        billable_orders_today: statsPayload?.billable_orders_today,
        total_agents: Number(statsPayload?.total_agents || 0),
        kg_washed_total: Number(statsPayload?.kg_washed_total || 0),
        kg_orders_delivered: Number(statsPayload?.kg_orders_delivered || 0),
        kg_orders_weighed: Number(statsPayload?.kg_orders_weighed || 0),
      })
      setLastUpdatedAt(Date.now())
    } catch (loadError) {
      setStatsError(getLoadMessage(loadError))
    } finally {
      setStatsLoading(false)
    }
  }, [])

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true)
    setAnalyticsError('')

    try {
      const analyticsResponse = await withServerTimeout(adminApi.getAnalytics())
      const analyticsPayload = unwrapPayload<AnalyticsData>(analyticsResponse)
      setAnalytics({
        labels: analyticsPayload?.labels || [],
        revenue: (analyticsPayload?.revenue || []).map((item) => Number(item || 0)),
        orders: (analyticsPayload?.orders || []).map((item) => Number(item || 0)),
      })
      setLastUpdatedAt(Date.now())
    } catch (loadError) {
      setAnalyticsError(getLoadMessage(loadError))
    } finally {
      setAnalyticsLoading(false)
    }
  }, [])

  const loadRecentOrders = useCallback(async () => {
    setOrdersLoading(true)
    setOrdersError('')

    try {
      const ordersResponse = await withServerTimeout(adminApi.getOrders({ limit: 7 }))
      const ordersPayload = unwrapPayload<OrdersPayload>(ordersResponse)
      setRecentOrders(Array.isArray(ordersPayload?.orders) ? ordersPayload.orders : [])
      setLastUpdatedAt(Date.now())
    } catch (loadError) {
      setOrdersError(getLoadMessage(loadError))
    } finally {
      setOrdersLoading(false)
    }
  }, [])

  const loadAlerts = useCallback(async () => {
    setAlertsLoading(true)
    setAlertsError('')

    try {
      const alertsResponse = await withServerTimeout(adminApi.getDashboardAlerts())
      const alertsPayload = unwrapPayload<{ alerts?: DashboardAlert[] }>(alertsResponse)
      setAlerts(Array.isArray(alertsPayload?.alerts) ? alertsPayload.alerts : [])
      setLastUpdatedAt(Date.now())
    } catch (loadError) {
      setAlertsError('Unable to load alerts')
    } finally {
      setAlertsLoading(false)
    }
  }, [])

  const loadDashboard = useCallback(() => {
    void loadStats()
    void loadAnalytics()
    void loadRecentOrders()
    void loadAlerts()
  }, [loadAlerts, loadAnalytics, loadRecentOrders, loadStats])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
    const timer = toastTimers.current[id]
    if (timer) {
      window.clearTimeout(timer)
      delete toastTimers.current[id]
    }
  }, [])

  const addToast = useCallback(
    (message: string, onClick?: () => void) => {
      const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`
      setToasts((prev) => [...prev, { id, message, onClick }])
      toastTimers.current[id] = window.setTimeout(() => {
        dismissToast(id)
      }, 5000)
    },
    [dismissToast]
  )

  useEffect(() => {
    return () => {
      Object.values(toastTimers.current).forEach((timer) => window.clearTimeout(timer))
      toastTimers.current = {}
    }
  }, [])

  const handleOrderCreated = useCallback(
    (payload: { order?: DashboardOrder }) => {
      if (!payload?.order) {
        return
      }

      new Audio('/notification.mp3').play().catch(() => {})

      const order = payload.order
      setRecentOrders((prev) => {
        const next = [order, ...prev.filter((item) => item.id !== order.id)]
        return next.slice(0, 7)
      })

      setStats((prev) => ({
        ...prev,
        orders_today: prev.orders_today + 1,
        // A new order adds to today's ORDER VALUE (it is placed), not to what
        // has been collected.
        order_value_today: Number(prev.order_value_today || 0) + Number(order.total || 0),
        pending_pickups: order.status === 'placed' ? prev.pending_pickups + 1 : prev.pending_pickups,
      }))

      setLastUpdatedAt(Date.now())

      addToast(`🧺 New order #${order.order_number || '-'} received!`, () => {
        const searchValue = order.order_number ? `?search=${encodeURIComponent(order.order_number)}` : ''
        navigate(`/orders${searchValue}`)
      })
    },
    [addToast, navigate]
  )

  // A payment or manual amount change moves collected/pending-COD and can move
  // today's order value. These are backend aggregates, so re-ask rather than
  // patch numbers locally and risk drift.
  const handlePaymentUpdated = useCallback(() => {
    void loadStats()
  }, [loadStats])

  const handleStatusChanged = useCallback(
    (payload: { order_id?: string; status?: string }) => {
      if (!payload?.order_id || !payload?.status) {
        return
      }

      setRecentOrders((prev) =>
        prev.map((order) =>
          order.id === payload.order_id ? { ...order, status: payload.status || order.status } : order,
        ),
      )

      void loadStats()
      void loadAlerts()
      setLastUpdatedAt(Date.now())
    },
    [loadAlerts, loadStats]
  )

  const handleAgentAssigned = useCallback(
    (payload: { order_id?: string; agent_name?: string }) => {
      if (!payload?.order_id) {
        return
      }

      setRecentOrders((prev) =>
        prev.map((order) =>
          order.id === payload.order_id
            ? {
                ...order,
                agent_name: payload.agent_name || order.agent_name,
                status: order.status === 'placed' ? 'agent_assigned' : order.status,
              }
            : order,
        ),
      )

      void loadStats()
      void loadAlerts()
      setLastUpdatedAt(Date.now())
    },
    [loadAlerts, loadStats]
  )

  const handleComplaintCreated = useCallback(
    (payload: { complaint?: { id?: string; category?: string } }) => {
      if (!payload?.complaint) {
        return
      }

      addToast(`🧾 New complaint (${payload.complaint.category || 'general'}) received`, () => {
        navigate('/complaints')
      })
    },
    [addToast, navigate]
  )

  const handleAgentStatusChanged = useCallback(
    (payload: { agent_id?: string; is_online?: boolean }) => {
      if (!payload?.agent_id || payload.is_online === undefined) {
        return
      }

      setStats((prev) => ({
        ...prev,
        active_agents: payload.is_online
          ? prev.active_agents + 1
          : Math.max(0, prev.active_agents - 1),
      }))
      setLastUpdatedAt(Date.now())
    },
    []
  )

  useSocket({
    'order:created': handleOrderCreated,
    'order:status_changed': handleStatusChanged,
    'order:agent_assigned': handleAgentAssigned,
    'order:payment_updated': handlePaymentUpdated,
    'order:cash_collected': handlePaymentUpdated,
    'complaint:created': handleComplaintCreated,
    'agent:status_changed': handleAgentStatusChanged,
  })

  useEffect(() => {
    void loadDashboard()
    const refreshTimer = window.setInterval(() => {
      void loadDashboard()
    }, 60000)

    const tickTimer = window.setInterval(() => {
      setTimeNow(Date.now())
    }, 1000)

    return () => {
      window.clearInterval(refreshTimer)
      window.clearInterval(tickTimer)
    }
  }, [loadDashboard])

  const filteredOrders = useMemo(() => {
    if (activeFilter === 'all') {
      return recentOrders
    }

    if (activeFilter === 'placed') {
      return recentOrders.filter((order) => order.status === 'placed')
    }

    if (activeFilter === 'in_progress') {
      return recentOrders.filter(
        (order) => ['agent_assigned', 'picked_up', 'at_facility', 'in_wash', 'in_iron', 'quality_check', 'ready', 'out_for_delivery'].includes(order.status),
      )
    }

    return recentOrders.filter((order) => order.status === 'delivered')
  }, [activeFilter, recentOrders])

  const handleQuickWhatsApp = async (order: DashboardOrder) => {
    try {
      const response = await adminApi.getWhatsAppLink(order.id, {
        template: 'order_confirmed_cod',
      })
      const payload = unwrapPayload<{ url?: string }>(response)
      if (payload?.url) {
        window.open(payload.url, '_blank')
        return
      }
    } catch (error) {
      console.error(error)
    }

    if (!order.customer_phone) {
      addToast('Customer phone is not available for WhatsApp.')
      return
    }

    const cleanPhone = order.customer_phone.replace(/[\s\-\+\(\)]/g, '')
    const phoneWithCountry = cleanPhone.startsWith('91') ? cleanPhone : `91${cleanPhone}`
    const fallbackMessage = encodeURIComponent(
      `Hi ${order.customer_name || 'there'}! Your laundry order #${order.order_number} has been placed.`,
    )
    window.open(`https://wa.me/${phoneWithCountry}?text=${fallbackMessage}`, '_blank')
  }

  const weeklyRevenueTotal = analytics.revenue.reduce((sum, value) => sum + Number(value || 0), 0)
  const sparklineData = analytics.revenue.map((value, index) => ({
    label: analytics.labels[index] || `${index + 1}`,
    value: Number(value || 0),
  }))

  const secondsSinceUpdate = Math.max(0, Math.floor((timeNow - lastUpdatedAt) / 1000))
  const hasAlerts = alerts.length > 0

  return (
    <div>
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
      <section
        className="dashboard-stats stat-cards-grid"
        style={{
          padding: '32px',
          display: 'flex',
          gap: '20px',
        }}
      >
        {statsLoading ? (
          <>
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </>
        ) : statsError ? (
          <div style={{ flex: 1 }}>
            <SectionError message={statsError} onRetry={() => void loadStats()} />
          </div>
        ) : (
          [
          {
              icon: ClipboardList,
              // Valid orders only. A cancelled order is not an order the
              // business has to fulfil, and counting it here made this tile
              // disagree with Today's Order Value beside it.
              value: String(stats.billable_orders_today ?? stats.orders_today),
              label: "Today's Orders",
              trendText: cancelledToday > 0
                ? `${cancelledToday} cancelled excluded`
                : `${stats?.orders_today || 0} placed`,
              trendBg: 'var(--color-info-bg)',
              trendColor: 'var(--color-info)',
              progressBg: colors.accent,
              progressWidth: '78%',
              valueColor: colors.textPrimary,
            },
            {
              icon: Users,
              value: String(stats.active_agents),
              label: 'Active Agents Online',
              trendText: `${stats?.active_agents || 0}/${stats?.total_agents || 0}`,
              trendBg: 'var(--color-info-bg)',
              trendColor: 'var(--color-info)',
              progressBg: colors.accent,
              progressWidth: '67%',
              valueColor: colors.textPrimary,
            },
            {
              // Value of today's orders at their final amounts - the exact
              // operational figure, not an abbreviated one.
              icon: IndianRupee,
              value: `₹${formatExactCurrency(stats.order_value_today || 0)}`,
              label: "Today's Order Value",
              trendText: 'placed today',
              trendBg: 'var(--color-info-bg)',
              trendColor: 'var(--color-info)',
              progressBg: colors.accent,
              progressWidth: '85%',
              valueColor: colors.textPrimary,
            },
            {
              // DIFFERENT metric: money actually received. Kept next to order
              // value so the two are never read as the same number.
              icon: IndianRupee,
              value: `₹${formatExactCurrency(stats.collected_today ?? stats.revenue_today)}`,
              label: 'Collected Today',
              trendText: 'received',
              trendBg: 'var(--color-success-bg)',
              trendColor: 'var(--color-success)',
              progressBg: colors.accent,
              progressWidth: '85%',
              valueColor: colors.textPrimary,
            },
            {
              icon: IndianRupee,
              value: `₹${formatExactCurrency(stats.pending_cod_amount || 0)}`,
              label: 'Pending COD',
              trendText: `${stats?.pending_cod_count || 0} orders`,
              trendBg: Number(stats?.pending_cod_count || 0) > 0
                ? 'var(--color-warning-bg)' : 'var(--color-success-bg)',
              trendColor: Number(stats?.pending_cod_count || 0) > 0
                ? 'var(--color-warning)' : 'var(--color-success)',
              progressBg: colors.accent,
              progressWidth: '100%',
              valueColor: colors.textPrimary,
            },
            ...(stats.delivered_unmarked_cod_count === undefined ? [] : [{
              icon: AlertTriangle,
              // The client's reported problem: delivered bags whose cash was
              // never marked collected. Already inside Pending COD above; broken
              // out because it is the part someone has to physically chase.
              value: `₹${formatExactCurrency(stats.delivered_unmarked_cod_amount || 0)}`,
              label: 'Delivered, COD Not Marked',
              trendText: `${stats?.delivered_unmarked_cod_count || 0} orders`,
              trendBg: Number(stats?.delivered_unmarked_cod_count || 0) > 0
                ? 'var(--color-error-bg)' : 'var(--color-success-bg)',
              trendColor: Number(stats?.delivered_unmarked_cod_count || 0) > 0
                ? 'var(--color-error)' : 'var(--color-success)',
              progressBg: colors.accent,
              progressWidth: '100%',
              valueColor: colors.textPrimary,
              onClick: () => navigate('/orders?delivered_unmarked_cod=1'),
            }]),
            {
              icon: Scale,
              value: `${formatCurrency(stats.kg_washed_total || 0)} kg`,
              label: 'Total Washed & Delivered',
              // How much of that total is a real hub weight rather than the
              // weight the order was booked at.
              trendText: `${stats?.kg_orders_weighed || 0}/${stats?.kg_orders_delivered || 0} weighed`,
              trendBg: 'var(--color-info-bg)',
              trendColor: 'var(--color-info)',
              progressBg: colors.accent,
              progressWidth: '100%',
              valueColor: colors.textPrimary,
            },
            {
              icon: Clock,
              value: String(stats.pending_pickups),
              label: 'Pending Assignment',
              trendText: Number(stats?.pending_pickups || 0) > 5 ? 'HIGH' : 'OK',
              trendBg: Number(stats?.pending_pickups || 0) > 5
                ? 'var(--color-error-bg)' : 'var(--color-success-bg)',
              trendColor: Number(stats?.pending_pickups || 0) > 5
                ? 'var(--color-error)' : 'var(--color-success)',
              progressBg: 'var(--color-error)',
              progressWidth: '100%',
              valueColor: 'var(--color-error)',
            },
          ].map((card: StatCard) => (
          <article
            key={card.label}
            onClick={card.onClick}
            role={card.onClick ? 'button' : undefined}
            tabIndex={card.onClick ? 0 : undefined}
            onKeyDown={card.onClick
              ? (event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    card.onClick?.()
                  }
                }
              : undefined}
            style={{
              flex: 1,
              cursor: card.onClick ? 'pointer' : undefined,
              background: colors.bgCard,
              border: `1px solid ${colors.border}`,
              borderRadius: '16px',
              padding: '20px 24px',
              boxShadow: CARD_SHADOW,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '999px',
                  background: colors.surface,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <card.icon size={20} color={colors.textPrimary} strokeWidth={2} />
              </div>
              <span
                className="font-body"
                style={{
                  borderRadius: '999px',
                  padding: '4px 10px',
                  background: card.trendBg,
                  color: card.trendColor,
                  fontSize: '11px',
                  fontWeight: 600,
                }}
              >
                {card.trendText}
              </span>
            </div>

            <div
              className="font-display"
              style={{
                fontSize: '32px',
                fontWeight: 700,
                lineHeight: 1,
                color: card.valueColor,
                marginBottom: '4px',
              }}
            >
              {card.value}
            </div>

            <p
              className="font-body"
              style={{
                fontSize: '13px',
                fontWeight: 400,
                color: colors.textSecondary,
                margin: '0 0 12px',
              }}
            >
              {card.label}
            </p>

            <div
              style={{
                height: '3px',
                borderRadius: '999px',
                background: colors.border,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: card.progressWidth,
                  height: '100%',
                  borderRadius: '999px',
                  background: card.progressBg,
                }}
              />
            </div>
          </article>
        ))
        )}
      </section>

      <section style={{ padding: '0 32px 24px' }}>
        <div
          style={{
            background: colors.surface,
            borderRadius: '16px',
            padding: '20px 24px',
            boxShadow: CARD_SHADOW,
            borderLeft: alertsError ? '4px solid #D97706' : hasAlerts ? '4px solid #DC2626' : '4px solid #16A34A',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '999px',
                  background: alertsError ? colors.bgSecondary : hasAlerts ? colors.bgSecondary : 'var(--color-success-bg)',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <AlertTriangle size={18} color={alertsError ? '#D97706' : hasAlerts ? '#DC2626' : '#16A34A'} />
              </div>
              <div>
                <h3 className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                  Action Required
                </h3>
                <p className="font-body" style={{ margin: 0, fontSize: '12px', color: colors.textMuted }}>
                  Last updated: {secondsSinceUpdate}s ago
                </p>
              </div>
            </div>
          </div>

          {alertsLoading ? (
            <div className="rapidry-pulse" style={{ height: '48px', borderRadius: '12px', background: colors.border }} />
          ) : alertsError ? (
            <SectionError message={alertsError} onRetry={() => void loadAlerts()} />
          ) : hasAlerts ? (
            <div style={{ display: 'grid', gap: '10px' }}>
              {alerts.map((alert) => {
                const isHigh = alert.severity === 'high'
                const accentColor = isHigh ? '#DC2626' : alert.severity === 'medium' ? '#D97706' : '#2563EB'
                const alertIcon =
                  alert.type === 'cod_collection' ? (
                    <Wallet size={16} color={accentColor} />
                  ) : isHigh ? (
                    <AlertCircle size={16} color={accentColor} />
                  ) : (
                    <AlertTriangle size={16} color={accentColor} />
                  )

                return (
                  <div
                    key={`${alert.type}-${alert.message}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '10px 12px',
                      borderRadius: '12px',
                      border: `1px solid ${accentColor}33`,
                      background: colors.surface,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>{alertIcon}</span>
                      <div>
                        <p className="font-body" style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: colors.textPrimary }}>
                          {alert.message}
                        </p>
                        {alert.count !== undefined ? (
                          <p className="font-body" style={{ margin: 0, fontSize: '11px', color: colors.textMuted }}>
                            {alert.count} orders
                          </p>
                        ) : null}
                      </div>
                    </div>
                    {alert.url ? (
                      <button
                        type="button"
                        className="font-body"
                        onClick={() => navigate(alert.url || '/orders')}
                        style={{
                          border: `1px solid ${accentColor}`,
                          color: accentColor,
                          background: 'transparent',
                          borderRadius: '999px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {alert.action || 'View Orders'}
                      </button>
                    ) : null}
                  </div>
                )
              })}
            </div>
          ) : (
            <div
              className="font-body"
              style={{
                background: 'var(--color-success-bg)',
                border: '1px solid var(--color-success-border)',
                color: 'var(--color-success)',
                borderRadius: '12px',
                padding: '12px',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              ✅ All clear. No pending actions
            </div>
          )}
        </div>
      </section>

      <section style={{ padding: '0 32px 8px' }}>
        <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '6px' }}>
          {(
            [
              {
                label: 'Create New Order',
                icon: Plus,
                onClick: () => navigate('/orders'),
              },
              {
                label: 'View Pending',
                icon: Clock,
                onClick: () => navigate('/orders?status=placed'),
              },
              {
                label: "View Today's Orders",
                icon: CalendarDays,
                onClick: () => navigate('/orders?date=today'),
              },
            ] satisfies Array<{
              label: string
              icon: ComponentType<{ size?: number }>
              onClick: () => void
            }>
          ).map((action) => (
            <button
              key={action.label}
              type="button"
              className="font-body"
              onClick={action.onClick}
              style={{
                border: `1.5px solid ${colors.border}`,
                background: 'transparent',
                color: colors.textPrimary,
                borderRadius: '999px',
                padding: '8px 14px',
                fontSize: '12px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.background = 'rgba(214,185,123,0.12)'
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.background = 'transparent'
              }}
            >
              <action.icon size={14} />
              {action.label}
            </button>
          ))}
        </div>
      </section>

      <section
        className="dashboard-columns"
        style={{
          padding: '0 32px 32px',
          display: 'flex',
          gap: '20px',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: '16px',
              padding: '24px',
              boxShadow: CARD_SHADOW,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '16px',
              }}
            >
              <h3
                className="font-display"
                style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: 600,
                  color: colors.textPrimary,
                }}
              >
                Recent Orders
              </h3>

              <button
                type="button"
                className="font-body"
                onClick={() => navigate('/orders')}
                style={{
                  border: 'none',
                  background: 'transparent',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: colors.accent,
                  cursor: 'pointer',
                  padding: 0,
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.opacity = '0.7'
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.opacity = '1'
                }}
              >
                View all →
              </button>
            </div>

            <div
              style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '16px',
              }}
            >
              {[
                { key: 'all', label: 'All' },
                { key: 'placed', label: 'Placed' },
                { key: 'in_progress', label: 'In Progress' },
                { key: 'delivered', label: 'Delivered' },
              ].map((item) => {
                const isActive = activeFilter === item.key

                return (
                  <button
                    key={item.key}
                    type="button"
                    className="font-body"
                    onClick={() => setActiveFilter(item.key as ActiveFilter)}
                    style={{
                      border: 'none',
                      borderRadius: '999px',
                      padding: '6px 14px',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      background: isActive ? colors.accent : colors.border,
                      color: isActive ? colors.textPrimary : colors.textSecondary,
                    }}
                  >
                    {item.label}
                  </button>
                )
              })}
            </div>

            <div>
              <div
                className="font-body"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.2fr 1.4fr 1.1fr 1.1fr 1fr 0.8fr',
                  gap: '8px',
                  padding: '12px 8px',
                  borderBottom: `1px solid ${colors.border}`,
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                <span>Order ID</span>
                <span>Customer</span>
                <span>Service</span>
                <span>Agent</span>
                <span>Status</span>
                <span>Amount</span>
              </div>

              {ordersLoading ? (
                <div className="rapidry-pulse" style={{ height: '180px', borderRadius: '12px', background: colors.border, marginTop: '12px' }} />
              ) : ordersError ? (
                <div style={{ marginTop: '12px' }}>
                  <SectionError message={ordersError} onRetry={() => void loadRecentOrders()} />
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="font-body" style={{ marginTop: '12px', padding: '32px 12px', textAlign: 'center', color: colors.textMuted, fontSize: '14px' }}>
                  No orders yet. New orders will appear here as customers place them.
                </div>
              ) : (
                filteredOrders.map((order) => (
                  <div
                    key={order.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 1.4fr 1.1fr 1.1fr 1fr 0.8fr',
                      gap: '8px',
                      padding: '16px 8px',
                      borderBottom: `1px solid ${colors.border}`,
                      alignItems: 'center',
                    }}
                    onMouseEnter={(event) => {
                      event.currentTarget.style.background = colors.bgSecondary
                    }}
                    onMouseLeave={(event) => {
                      event.currentTarget.style.background = 'transparent'
                    }}
                  >
                    <span
                      className="font-body"
                      style={{ fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}
                    >
                      #{order.order_number}
                    </span>
                    <span
                      className="font-body"
                      style={{ fontSize: '13px', fontWeight: 400, color: colors.textPrimary }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                        <span>{order.customer_name || '-'}</span>
                        {order.status === 'placed' ? (
                          <button
                            type="button"
                            aria-label="Send WhatsApp confirmation"
                            onClick={() => {
                              void handleQuickWhatsApp(order)
                            }}
                            style={{
                              width: '26px',
                              height: '26px',
                              borderRadius: '8px',
                              border: 'none',
                              background: '#25D366',
                              color: '#FFFFFF',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'grid',
                              placeItems: 'center',
                            }}
                            onMouseEnter={(event) => {
                              event.currentTarget.style.background = '#1EAD54'
                            }}
                            onMouseLeave={(event) => {
                              event.currentTarget.style.background = '#25D366'
                            }}
                          >
                            <MessageCircle size={12} />
                          </button>
                        ) : null}
                      </span>
                    </span>
                    <span
                      className="font-body"
                      style={{ fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}
                    >
                      {getFirstService(order)}
                    </span>
                    <span
                      className="font-body"
                      style={{ fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}
                    >
                      {order.agent_name || '-'}
                    </span>
                    <span>
                      <span
                        className="font-body"
                        style={{
                          ...getStatusBadgeStyle(order.status),
                          display: 'inline-block',
                          borderRadius: '999px',
                          padding: '4px 10px',
                          fontSize: '11px',
                          fontWeight: 500,
                        }}
                      >
                        {toTitleCase(order.status)}
                      </span>
                    </span>
                    <span
                      className="font-body"
                      style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}
                    >
                      ₹{formatCurrency(order.total)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div
          className="dashboard-right-panel"
          style={{
            width: '340px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}
        >
          <article
            style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: '16px',
              padding: '20px',
              boxShadow: CARD_SHADOW,
            }}
          >
            <h3
              className="font-display"
              style={{
                margin: '0 0 12px',
                fontSize: '16px',
                fontWeight: 600,
                color: colors.textPrimary,
              }}
            >
              {stats.active_agents} agents online
            </h3>

            <div style={{ display: 'flex', alignItems: 'center' }}>
              {Array.from({ length: Math.min(Number(stats.active_agents || 0), 5) }).map((_, index) => (
                <div
                  key={index}
                  className="font-body"
                  aria-hidden="true"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '999px',
                    background: colors.accent,
                    border: `2px solid ${colors.surface}`,
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: '14px',
                    marginLeft: index === 0 ? '0' : '-8px',
                  }}
                >
                  👤
                </div>
              ))}
              {Number(stats.active_agents || 0) > 5 ? (
                <span className="font-body" style={{ marginLeft: '8px', fontSize: '12px', color: colors.textMuted }}>
                  +{Number(stats.active_agents || 0) - 5}
                </span>
              ) : null}
            </div>

            <p
              className="font-body"
              style={{
                margin: '12px 0 0',
                fontSize: '13px',
                fontWeight: 400,
                color: colors.textSecondary,
              }}
            >
              5 on pickup · 3 idle
            </p>
          </article>

          <article
            className="dashboard-chart"
            style={{
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: '16px',
              padding: '20px',
              boxShadow: CARD_SHADOW,
            }}
          >
            {analyticsLoading ? (
              <div className="rapidry-pulse" style={{ height: '112px', borderRadius: '12px', background: colors.border }} />
            ) : analyticsError ? (
              <SectionError message={analyticsError} onRetry={() => void loadAnalytics()} />
            ) : (
              <>
                <div
                  className="font-display"
                  style={{
                    fontSize: '24px',
                    fontWeight: 700,
                    color: colors.textPrimary,
                    marginBottom: '4px',
                    lineHeight: 1.15,
                  }}
                >
                  ₹{formatCurrency(weeklyRevenueTotal)}
                </div>

                <p
                  className="font-body"
                  style={{
                    margin: '0 0 16px',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: colors.textSecondary,
                  }}
                >
                  This week&apos;s revenue
                </p>

                <div style={{ width: '100%', height: '60px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={sparklineData}>
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke={colors.accent}
                        strokeWidth={2}
                        fill="rgba(214,185,123,0.2)"
                        fillOpacity={0.5}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </>
            )}
          </article>

          <article
            style={{
              background: colors.bgSidebar,
              border: `1px solid ${colors.border}`,
              borderRadius: '16px',
              padding: '20px',
            }}
          >
            <h3
              className="font-display"
              style={{
                margin: '0 0 12px',
                fontSize: '15px',
                fontWeight: 600,
                color: 'var(--color-cream)',
              }}
            >
              {stats.pending_pickups} orders pending agent
            </h3>

            <button
              type="button"
              className="font-body"
              onClick={() => navigate('/orders?status=placed')}
              style={{
                width: '100%',
                background: colors.accent,
                color: colors.textPrimary,
                border: 'none',
                borderRadius: '999px',
                padding: '10px 16px',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Assign now →
            </button>
          </article>
        </div>
      </section>
    </div>
  )
}
